import $ from 'jquery'
import { Util } from 'bootstrap'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'alerts'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Selector = {
  ALERTS: '.alerts',
  ALERT: '> .alert',
  ALERT_PRIMARY: '.alert-primary',
  ALERT_SECONDARY: '.alert-secondary',
  ALERT_SUCCESS: '.alert-success',
  ALERT_INFO: '.alert-info',
  ALERT_WARNING: '.alert-warning',
  ALERT_DANGER: '.alert-danger',
  ALERT_LIGHT: '.alert-light',
  ALERT_DARK: '.alert-dark',
  DATA_TOGGLE: '[data-toggle="alerts"]'
}

const Event = {
  ALERT_DATA_API: `alert${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Alerts {
  constructor(element) {
    this._element = element
    this._alertNodes = this._getAlertNodes()
  }

  // Public

  alert(message, alertSel = Selector.ALERT_INFO) {
    const $el = $(this._alertNodes).filter(alertSel).first().clone()
    if ($el.length > 0) {
      $('<span>').text(message).appendTo($el)
      $el.collapse('hide')
      $el.appendTo(this._element)
      $el.collapse('show')
    } else {
      alert(message) // eslint-disable-line no-alert
    }
  }

  primary(message) {
    this.alert(message, Selector.ALERT_PRIMARY)
  }

  secondary(message) {
    this.alert(message, Selector.ALERT_SECONDARY)
  }

  success(message) {
    this.alert(message, Selector.ALERT_SUCCESS)
  }

  info(message) {
    this.alert(message, Selector.ALERT_INFO)
  }

  warning(message) {
    this.alert(message, Selector.ALERT_WARNING)
  }

  danger(message) {
    this.alert(message, Selector.ALERT_DANGER)
  }

  light(message) {
    this.alert(message, Selector.ALERT_LIGHT)
  }

  dark(message) {
    this.alert(message, Selector.ALERT_DARK)
  }

  // Private

  _getAlertNodes() {
    return $(this._element).find(Selector.ALERT).detach()
  }

  // Static

  static trigger(el, message, method = 'alert') {
    $(el).trigger(Event.ALERT_DATA_API, [message, method])
  }

  static _jQueryInterface(config, ...args) {
    if (config === 'trigger') {
      Alerts.trigger(this, ...args)
      return this
    }

    return this.each(function () {
      let data = $(this).data(DATA_KEY)

      if (!data) {
        data = new Alerts(this)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

$(document).on(Event.ALERT_DATA_API, Selector.DATA_TOGGLE, function (event, message, method = 'alert') {
  const selector = Util.getSelectorFromElement(this)
  const target = selector ?
    document.querySelector(selector) :
    this.querySelector(Selector.ALERTS)

  if (target) {
    Alerts._jQueryInterface.call($(target), method, message)

    // Mark event as handled
    event.stopImmediatePropagation()
    event.preventDefault()
    return false
  }
})

// Fallback to js alert function if event was not handled.
$(document).on(Event.ALERT_DATA_API, (event, message) => {
  if (message) {
    alert(message) // eslint-disable-line no-alert
  }
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = Alerts._jQueryInterface
$.fn[NAME].Constructor = Alerts
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return Alerts._jQueryInterface
}

export default Alerts
